import {
  Alert,
  Button,
  Code,
  createStyles,
  Divider,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import React, {useEffect, useRef, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import getTestProps from 'test/lib/cypress/getTestProps';
import useAsyncState, {getMostRecentData} from 'ui-library/hooks/useAsyncState';
import copyToClipboard from 'util/clipboard/copyToClipboard';
import * as net from 'util/Net';

import type {BoltzApiKeys, BoltzApiSpendSummary} from './BoltzApiService';
import {
  BOLTZ_CREDIT_INCREMENT,
  BOLTZ_MAX_CREDITS_PER_TRANSFER,
  BOLTZ_SPEND_PER_CREDIT_INCREMENT_USD,
  getBoltzApiSpendSummary,
  provisionBoltzApiKeys,
  transferBoltzCredits,
} from './BoltzApiService';
import BoltzApiSpendUsage from './BoltzApiSpendUsage';

const TEST_COMPONENT = 'BoltzApiConfigureModal';

const useStyles = createStyles(() => ({
  apiKeyValue: {
    flex: 1,
    overflowX: 'auto',
    whiteSpace: 'nowrap',
  },
}));

export interface BoltzApiConfigureModalProps {
  opened: boolean;
  onClose: () => void;
}

function formatCredits(credits: number): string {
  return credits.toLocaleString();
}

export function buildTransferOptions(): Array<{value: string; label: string}> {
  const options: Array<{value: string; label: string}> = [];
  for (
    let credits = BOLTZ_CREDIT_INCREMENT;
    credits <= BOLTZ_MAX_CREDITS_PER_TRANSFER;
    credits += BOLTZ_CREDIT_INCREMENT
  ) {
    options.push({value: String(credits), label: `${formatCredits(credits)} credits`});
  }
  return options;
}

const TRANSFER_OPTIONS = buildTransferOptions();

const HTTP_STATUS_PAYMENT_REQUIRED = 402;

function getTransferErrorMessage(e: unknown): string {
  // The backend returns a 402 when the tenant doesn't have enough Benchling credits for the transfer.
  if (net.isServerRequestError(e) && (e as {status?: number}).status === HTTP_STATUS_PAYMENT_REQUIRED) {
    return net.getUserMessage(e) ?? 'You don’t have enough Benchling credits to complete this transfer.';
  }
  return net.getUserMessage(e) ?? 'We couldn’t complete the transfer. Please try again.';
}

function ApiKeyRow({
  label,
  value,
  copyLabel,
}: {
  label: string;
  value: string;
  copyLabel: string;
}): JSX.Element {
  const {classes} = useStyles();
  return (
    <Stack spacing={4}>
      <Text size="sm" weight={600}>
        {label}
      </Text>
      <Group spacing="xs" noWrap={true} align="center">
        <Code
          block={true}
          className={classes.apiKeyValue}
          {...getTestProps({component: TEST_COMPONENT, element: 'apiKeyValue', key: label})}
        >
          {value}
        </Code>
        <Button
          size="xs"
          variant="secondary"
          aria-label={copyLabel}
          onClick={() => {
            copyToClipboard(value, {showToastOnSuccess: true, toastText: `${label} copied`}).catch(() => {});
          }}
        >
          Copy
        </Button>
      </Group>
    </Stack>
  );
}

export function BoltzApiConfigureModal({opened, onClose}: BoltzApiConfigureModalProps): JSX.Element {
  const [selectedCredits, setSelectedCredits] = useState<string>(String(BOLTZ_CREDIT_INCREMENT));
  const [isConfirming, setIsConfirming] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [transferError, setTransferError] = useState<string | null>(null);
  const [transferSuccess, setTransferSuccess] = useState<string | null>(null);

  const [apiKeys, setApiKeys] = useState<BoltzApiKeys | null>(null);
  const [generatingKeys, setGeneratingKeys] = useState(false);
  const [keysError, setKeysError] = useState<string | null>(null);

  const spendLimitEnabled = BenchlingConfig.MODEL_HUB_ENABLE_BOLTZ_API_SPEND_LIMIT;
  // useAsyncState with keepOnlyLatest cancels any in-flight request when a newer one starts, so the
  // latest fetch always wins (refresh runs on open and again after a transfer raises the limit).
  const {currentState: spendSummaryState, makeCall: refreshSpendSummary} = useAsyncState<
    [],
    BoltzApiSpendSummary
  >({
    makeCall: getBoltzApiSpendSummary,
    keepOnlyLatest: true,
  });
  // Preserve the last successfully-loaded summary so a failed (or superseded) refresh doesn't wipe a
  // good — if perhaps stale — value; getMostRecentData also keeps it visible while a refresh loads.
  const lastLoadedSpendSummary = useRef<BoltzApiSpendSummary | null>(null);
  const recentSpendSummary = getMostRecentData(spendSummaryState);
  if (recentSpendSummary != null) {
    lastLoadedSpendSummary.current = recentSpendSummary;
  }
  const spendSummary = recentSpendSummary ?? lastLoadedSpendSummary.current;

  useEffect(() => {
    if (!opened) {
      return;
    }
    setSelectedCredits(String(BOLTZ_CREDIT_INCREMENT));
    setIsConfirming(false);
    setTransferring(false);
    setTransferError(null);
    setTransferSuccess(null);
    setApiKeys(null);
    setGeneratingKeys(false);
    setKeysError(null);
    if (spendLimitEnabled) {
      refreshSpendSummary();
    }
  }, [opened, refreshSpendSummary, spendLimitEnabled]);

  const handleStartTransfer = (): void => {
    setTransferError(null);
    setTransferSuccess(null);
    setIsConfirming(true);
  };

  const handleConfirmTransfer = (): void => {
    const credits = Number(selectedCredits);
    setTransferring(true);
    setTransferError(null);
    transferBoltzCredits(credits)
      .then(() => {
        setTransferSuccess(`${formatCredits(credits)} Benchling credits were transferred to the Boltz API.`);
        setIsConfirming(false);
        setTransferring(false);
        // A successful transfer raises the spending limit, so refresh the displayed value.
        if (spendLimitEnabled) {
          refreshSpendSummary();
        }
      })
      .catch((e: unknown) => {
        setTransferError(getTransferErrorMessage(e));
        setIsConfirming(false);
        setTransferring(false);
      });
  };

  const handleGenerateKeys = (): void => {
    setGeneratingKeys(true);
    setKeysError(null);
    provisionBoltzApiKeys()
      .then((keys) => {
        setApiKeys(keys);
        setGeneratingKeys(false);
      })
      .catch((e: unknown) => {
        setKeysError(net.getUserMessage(e) ?? 'We couldn’t generate API keys. Please try again.');
        setGeneratingKeys(false);
      });
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Configure Boltz API" size="lg">
      <Stack spacing="lg" {...getTestProps({component: TEST_COMPONENT, element: 'root'})}>
        <Stack spacing="sm">
          <Title order={5}>Transfer Benchling credits</Title>
          <Text size="sm" color="dimmed">
            Transfer Benchling credits to the Boltz API to fund usage. Credits are transferred in increments
            of {formatCredits(BOLTZ_CREDIT_INCREMENT)}, up to {formatCredits(BOLTZ_MAX_CREDITS_PER_TRANSFER)}{' '}
            at a time. Every {formatCredits(BOLTZ_CREDIT_INCREMENT)} Benchling credits equals{' '}
            {`$${BOLTZ_SPEND_PER_CREDIT_INCREMENT_USD}`} in allocated Boltz API spend. Once your Boltz API
            spend limit is reached, the API will prevent further requests until more credits are transferred.
            This is a one-time transfer and can’t be reversed.
          </Text>

          {spendLimitEnabled && spendSummary != null && <BoltzApiSpendUsage spendSummary={spendSummary} />}

          {transferSuccess != null && (
            <Alert color="green" variant="light">
              {transferSuccess}
            </Alert>
          )}
          {transferError != null && (
            <Alert color="red" variant="light">
              {transferError}
            </Alert>
          )}

          {!isConfirming && (
            <Group align="flex-end" spacing="sm">
              <Select
                label="Credits to transfer"
                data={TRANSFER_OPTIONS}
                value={selectedCredits}
                onChange={(value) => {
                  if (value != null) {
                    setSelectedCredits(value);
                  }
                }}
                withinPortal={true}
                {...getTestProps({component: TEST_COMPONENT, element: 'creditsSelect'})}
              />
              <Button onClick={handleStartTransfer}>Transfer credits</Button>
            </Group>
          )}

          {isConfirming && (
            <Alert color="orange" variant="light" title="Confirm credit transfer">
              <Stack spacing="sm">
                <Text size="sm">
                  You’re about to transfer {formatCredits(Number(selectedCredits))} Benchling credits to the
                  Boltz API. This is a one-time transfer and can’t be reversed.
                </Text>
                <Group spacing="sm">
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setIsConfirming(false);
                    }}
                    disabled={transferring}
                  >
                    Cancel
                  </Button>
                  <Button color="orange" loading={transferring} onClick={handleConfirmTransfer}>
                    Confirm transfer
                  </Button>
                </Group>
              </Stack>
            </Alert>
          )}
        </Stack>

        <Divider />

        <Stack spacing="sm">
          <Title order={5}>API keys</Title>
          <Text size="sm" color="dimmed">
            Generate API keys to authenticate your requests to the Boltz API.
          </Text>

          {keysError != null && (
            <Alert color="red" variant="light">
              {keysError}
            </Alert>
          )}

          {apiKeys == null ? (
            <Group>
              <Button loading={generatingKeys} onClick={handleGenerateKeys}>
                Generate API key
              </Button>
            </Group>
          ) : (
            <Stack spacing="md">
              <Alert color="yellow" variant="light" title="Save your keys now">
                Copy and store these keys somewhere safe. For your security, you won’t be able to view them
                again after you close this modal. To revoke old keys, contact support.
              </Alert>
              <ApiKeyRow label="Live API key" value={apiKeys.liveApiKey} copyLabel="Copy live API key" />
              <ApiKeyRow label="Test API key" value={apiKeys.testApiKey} copyLabel="Copy test API key" />
            </Stack>
          )}
        </Stack>
      </Stack>
    </Modal>
  );
}
